import { useMemo } from "react";
import { get } from "lodash";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import NoteTemplateService from "services/NoteTemplate";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";
import { NOTE_TEMPLATE_TABLE_CONSTANTS } from "routes/note_template/NoteTemplateTableConstants";
import { getNoteTemplateTableColumns } from "routes/note_template/NoteTemplateTableColumnsAndFilters";

export const invalidateNoteTemplateQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [NOTE_TEMPLATE_TABLE_CONSTANTS.queryKey],
  });
};

export const useNoteTemplateMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data => NoteTemplateService.create(data),
    onSuccess: () => invalidateNoteTemplateQuery(),
    onError: err =>
      toast.error(formatError(err, "Failed to save note template")),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => NoteTemplateService.update(id, data),
    onSuccess: (_data, { id }) => {
      invalidateNoteTemplateQuery();
      queryClient.invalidateQueries({ queryKey: ["note_template", id] });
    },
    onError: err =>
      toast.error(formatError(err, "Failed to save note template")),
  });

  const remove = useMutation({
    mutationFn: id => NoteTemplateService.delete(id),
    onSuccess: () => {
      invalidateNoteTemplateQuery();
      toast.success("Note template deleted");
      onDeleted?.();
    },
    onError: err =>
      toast.error(formatError(err, "Failed to delete note template")),
  });

  return { create, update, remove };
};

export const useNoteTemplateTable = ({ tableName }) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getNoteTemplateTableColumns();
    const noteTemplateTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    return addResizedColumns({
      columns: initialColumns,
      resizedColumns: noteTemplateTableSettings?.resized_columns || {},
    });
  }, [tableName, tableSettings, isTableSettingsFetched]);

  return { columns };
};
